import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { api } from '../../utils/apiClient';
import { Card, Button, Pill, Sheet, EmptyState, SectionHeader } from '../../components/ui';
import './MonetizationPanel.css';

interface Props {
  restaurantId: string;
}

interface BillingData {
  balance: number;
  subscriptionTier: 'FREE' | 'PRO' | 'PREMIUM';
  restaurantName: string;
  autoRechargeEnabled: boolean;
  autoRechargeThreshold: number | null;
  autoRechargeAmount: number | null;
  transactions: BillingTransaction[];
}

interface BillingTransaction {
  id: string;
  type: 'TOPUP' | 'BOOST_SPEND' | 'SUBSCRIPTION_CHARGE';
  amount: number;
  status: 'PENDING' | 'COMPLETED' | 'FAILED' | 'REFUNDED';
  description: string | null;
  createdAt: string;
}

interface BoostCampaign {
  id: string;
  bidAmount: number;
  startsAt: string;
  endsAt: string;
  status: 'PENDING' | 'ACTIVE' | 'PAUSED' | 'COMPLETED' | 'CANCELLED';
  totalSpend: number;
}

type SectionName = 'overview' | 'campaigns' | 'balance' | 'banner-slots';

export default function MonetizationPanel({ restaurantId }: Props) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const isDeveloperOrSuperAdmin = user?.role === 'DEVELOPER' || user?.role === 'SUPER_ADMIN';

  const [activeSection, setActiveSection] = useState<SectionName>('overview');
  const [billing, setBilling] = useState<BillingData | null>(null);
  const [campaigns, setCampaigns] = useState<BoostCampaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newBidAmount, setNewBidAmount] = useState('');
  const [newStartsAt, setNewStartsAt] = useState('');
  const [newEndsAt, setNewEndsAt] = useState('');
  const [createError, setCreateError] = useState('');
  const [creating, setCreating] = useState(false);

  const [savingRecharge, setSavingRecharge] = useState(false);

  const fetchData = async () => {
    if (!restaurantId) return;
    setLoading(true);
    setError('');
    try {
      const params = isDeveloperOrSuperAdmin ? { params: { restaurantId } } : {};
      const [billingRes, campaignsRes] = await Promise.all([
        api.get<BillingData>('/api/monetization/billing', params),
        api.get<{ campaigns: BoostCampaign[] }>('/api/monetization/boost-campaigns', params),
      ]);
      setBilling(billingRes.data);
      setCampaigns(campaignsRes.data.campaigns);
    } catch (err: any) {
      setError(err.response?.data?.error || t('common.somethingWentWrong'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restaurantId]);

  const handleCreateCampaign = async () => {
    setCreateError('');
    const bid = parseFloat(newBidAmount);
    if (!bid || bid <= 0) {
      setCreateError(t('monetization.campaigns.errorBidPositive'));
      return;
    }
    const start = new Date(newStartsAt);
    const end = new Date(newEndsAt);
    if (isNaN(start.getTime()) || start <= new Date()) {
      setCreateError(t('monetization.campaigns.errorStartFuture'));
      return;
    }
    if (isNaN(end.getTime()) || end <= start) {
      setCreateError(t('monetization.campaigns.errorEndAfterStart'));
      return;
    }

    setCreating(true);
    try {
      const body: Record<string, unknown> = {
        bidAmount: bid,
        startsAt: start.toISOString(),
        endsAt: end.toISOString(),
      };
      if (isDeveloperOrSuperAdmin) body.restaurantId = restaurantId;
      await api.post('/api/monetization/boost-campaigns', body);
      setShowCreateModal(false);
      setNewBidAmount('');
      setNewStartsAt('');
      setNewEndsAt('');
      fetchData();
    } catch (err: any) {
      setCreateError(err.response?.data?.error || t('common.somethingWentWrong'));
    } finally {
      setCreating(false);
    }
  };

  const handleCancelCampaign = async (id: string) => {
    try {
      await api.patch(`/api/monetization/boost-campaigns/${id}`, { status: 'CANCELLED' });
      fetchData();
    } catch (err: any) {
      setError(err.response?.data?.error || t('common.somethingWentWrong'));
    }
  };

  const handleAutoRechargeToggle = async (enabled: boolean) => {
    if (!billing) return;
    setSavingRecharge(true);
    try {
      const body: Record<string, unknown> = {
        autoRechargeEnabled: enabled,
        autoRechargeThreshold: billing.autoRechargeThreshold,
        autoRechargeAmount: billing.autoRechargeAmount,
      };
      if (isDeveloperOrSuperAdmin) body.restaurantId = restaurantId;
      await api.patch('/api/monetization/billing/auto-recharge', body);
      setBilling({ ...billing, autoRechargeEnabled: enabled });
    } catch (err: any) {
      setError(err.response?.data?.error || t('common.somethingWentWrong'));
    } finally {
      setSavingRecharge(false);
    }
  };

  const tierTone = (tier: string): 'green' | 'amber' | 'neutral' => {
    if (tier === 'PREMIUM') return 'green';
    if (tier === 'PRO') return 'amber';
    return 'neutral';
  };

  const formatKZT = (amount: number) => `${Math.abs(amount).toLocaleString('ru-RU')} ₸`;
  const formatDate = (iso: string) => new Date(iso).toLocaleDateString('ru-RU');

  const SECTIONS: SectionName[] = ['overview', 'campaigns', 'balance', 'banner-slots'];

  if (loading) return <div className="mon-loading">{t('common.loading')}</div>;
  if (error) return <div className="mon-error">{error}</div>;
  if (!billing) return (
    <EmptyState
      icon="💳"
      title={t('monetization.emptyState.title')}
      sub={t('monetization.emptyState.subtitle')}
    />
  );

  const activeCampaigns = campaigns.filter(c => c.status === 'ACTIVE' || c.status === 'PENDING');

  return (
    <div className="mon-panel">
      {/* Section navigation */}
      <div className="mon-sections">
        {SECTIONS.map(s => (
          <button
            key={s}
            className={`mon-section-btn${activeSection === s ? ' active' : ''}`}
            onClick={() => setActiveSection(s)}
          >
            {t(`monetization.section.${s}`)}
          </button>
        ))}
      </div>

      {/* Overview section */}
      {activeSection === 'overview' && (
        <div className="mon-overview">
          <Card className="mon-tier-card">
            <SectionHeader label={t('monetization.overview.subscription')} />
            <div className="mon-tier-row">
              <Pill tone={tierTone(billing.subscriptionTier)}>
                {billing.subscriptionTier}
              </Pill>
              {isDeveloperOrSuperAdmin && (
                <select
                  className="mon-tier-select"
                  value={billing.subscriptionTier}
                  onChange={async (e) => {
                    try {
                      await api.patch('/api/monetization/subscription', {
                        restaurantId,
                        tier: e.target.value,
                      });
                      setBilling({
                        ...billing,
                        subscriptionTier: e.target.value as 'FREE' | 'PRO' | 'PREMIUM',
                      });
                    } catch (err: any) {
                      setError(err.response?.data?.error || t('common.somethingWentWrong'));
                    }
                  }}
                >
                  <option value="FREE">FREE</option>
                  <option value="PRO">PRO</option>
                  <option value="PREMIUM">PREMIUM</option>
                </select>
              )}
            </div>
          </Card>

          <Card className="mon-balance-card">
            <SectionHeader label={t('monetization.overview.balance')} />
            <div className="mon-balance-amount">{formatKZT(billing.balance)}</div>
          </Card>

          <Card className="mon-campaigns-summary">
            <SectionHeader label={t('monetization.overview.activeCampaigns')} />
            <div className="mon-campaigns-count">{activeCampaigns.length}</div>
            {billing.subscriptionTier === 'FREE' && (
              <p className="mon-upgrade-hint">{t('monetization.overview.upgradeHint')}</p>
            )}
          </Card>
        </div>
      )}

      {/* Campaigns section */}
      {activeSection === 'campaigns' && (
        <div className="mon-campaigns">
          <div className="mon-section-header-row">
            <SectionHeader label={t('monetization.campaigns.title')} />
            {billing.subscriptionTier !== 'FREE' && (
              <Button variant="primary" size="sm" onClick={() => setShowCreateModal(true)}>
                {t('monetization.campaigns.create')}
              </Button>
            )}
          </div>

          {campaigns.length === 0 ? (
            <EmptyState
              title={t('monetization.campaigns.empty.title')}
              sub={t('monetization.campaigns.empty.subtitle')}
            />
          ) : (
            <div className="mon-campaigns-list">
              {campaigns.map(campaign => (
                <Card key={campaign.id} className="mon-campaign-card">
                  <div className="mon-campaign-header">
                    <Pill
                      tone={
                        campaign.status === 'ACTIVE'
                          ? 'green'
                          : campaign.status === 'PAUSED'
                          ? 'amber'
                          : 'neutral'
                      }
                    >
                      {t(`monetization.campaigns.status.${campaign.status.toLowerCase()}`)}
                    </Pill>
                    <span className="mon-campaign-bid">
                      {formatKZT(campaign.bidAmount)}/{t('monetization.campaigns.perDay')}
                    </span>
                  </div>
                  <div className="mon-campaign-dates">
                    {formatDate(campaign.startsAt)} — {formatDate(campaign.endsAt)}
                  </div>
                  <div className="mon-campaign-spend">
                    {t('monetization.campaigns.totalSpend')}: {formatKZT(campaign.totalSpend)}
                  </div>
                  {(campaign.status === 'ACTIVE' || campaign.status === 'PENDING') && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleCancelCampaign(campaign.id)}
                    >
                      {t('monetization.campaigns.cancel')}
                    </Button>
                  )}
                </Card>
              ))}
            </div>
          )}

          <Sheet open={showCreateModal} onClose={() => setShowCreateModal(false)}>
            <div className="mon-create-modal">
              <h3 className="mon-create-title">{t('monetization.campaigns.createModal.title')}</h3>
              <div className="mon-create-field">
                <label>
                  {t('monetization.campaigns.createModal.bidAmount')} (₸/{t('monetization.campaigns.perDay')})
                </label>
                <input
                  type="number"
                  min="1"
                  value={newBidAmount}
                  onChange={e => setNewBidAmount(e.target.value)}
                  placeholder="500"
                  className="mon-input"
                />
              </div>
              <div className="mon-create-field">
                <label>{t('monetization.campaigns.createModal.startsAt')}</label>
                <input
                  type="datetime-local"
                  value={newStartsAt}
                  onChange={e => setNewStartsAt(e.target.value)}
                  className="mon-input"
                />
              </div>
              <div className="mon-create-field">
                <label>{t('monetization.campaigns.createModal.endsAt')}</label>
                <input
                  type="datetime-local"
                  value={newEndsAt}
                  onChange={e => setNewEndsAt(e.target.value)}
                  className="mon-input"
                />
              </div>
              {createError && <p className="mon-create-error">{createError}</p>}
              <div className="mon-create-actions">
                <Button
                  variant="secondary"
                  size="md"
                  onClick={() => setShowCreateModal(false)}
                >
                  {t('common.cancel')}
                </Button>
                <Button
                  variant="primary"
                  size="md"
                  onClick={handleCreateCampaign}
                  disabled={creating}
                >
                  {creating ? t('common.loading') : t('monetization.campaigns.createModal.submit')}
                </Button>
              </div>
            </div>
          </Sheet>
        </div>
      )}

      {/* Balance section */}
      {activeSection === 'balance' && (
        <div className="mon-balance">
          <Card className="mon-balance-card">
            <SectionHeader label={t('monetization.balance.current')} />
            <div className="mon-balance-amount">{formatKZT(billing.balance)}</div>
          </Card>

          <Card className="mon-recharge-card">
            <SectionHeader label={t('monetization.balance.autoRecharge')} />
            <div className="mon-recharge-toggle-row">
              <span>{t('monetization.balance.autoRechargeEnabled')}</span>
              <button
                className={`mon-toggle${billing.autoRechargeEnabled ? ' active' : ''}`}
                onClick={() => handleAutoRechargeToggle(!billing.autoRechargeEnabled)}
                disabled={savingRecharge}
                aria-label={t('monetization.balance.autoRechargeEnabled')}
              />
            </div>
            {billing.autoRechargeEnabled && (
              <div className="mon-recharge-details">
                <p>
                  {t('monetization.balance.threshold')}:{' '}
                  {billing.autoRechargeThreshold ? formatKZT(billing.autoRechargeThreshold) : '—'}
                </p>
                <p>
                  {t('monetization.balance.rechargeAmount')}:{' '}
                  {billing.autoRechargeAmount ? formatKZT(billing.autoRechargeAmount) : '—'}
                </p>
              </div>
            )}
          </Card>

          <Card className="mon-transactions-card">
            <SectionHeader label={t('monetization.balance.transactions')} />
            {billing.transactions.length === 0 ? (
              <p className="mon-empty-text">{t('monetization.balance.noTransactions')}</p>
            ) : (
              <div className="mon-tx-list">
                {billing.transactions.map(tx => (
                  <div key={tx.id} className="mon-tx-row">
                    <div className="mon-tx-left">
                      <Pill
                        tone={
                          tx.status === 'COMPLETED'
                            ? 'green'
                            : tx.status === 'FAILED'
                            ? 'red'
                            : 'neutral'
                        }
                      >
                        {t(`monetization.tx.type.${tx.type.toLowerCase()}`)}
                      </Pill>
                      <span className="mon-tx-desc">{tx.description || ''}</span>
                    </div>
                    <div className="mon-tx-right">
                      <span className={`mon-tx-amount${tx.amount >= 0 ? ' positive' : ' negative'}`}>
                        {tx.amount >= 0 ? '+' : ''}{formatKZT(tx.amount)}
                      </span>
                      <span className="mon-tx-date">{formatDate(tx.createdAt)}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>
      )}

      {/* Banner Slots section */}
      {activeSection === 'banner-slots' && (
        <div className="mon-banner-slots">
          {billing.subscriptionTier !== 'PREMIUM' ? (
            <EmptyState
              title={t('monetization.bannerSlots.premiumOnly.title')}
              sub={t('monetization.bannerSlots.premiumOnly.subtitle')}
            />
          ) : (
            <Card>
              <SectionHeader label={t('monetization.bannerSlots.title')} />
              <p className="mon-empty-text">{t('monetization.bannerSlots.description')}</p>
            </Card>
          )}
        </div>
      )}
    </div>
  );
}
